import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import Joi from "joi";
import axios from "axios";
import confetti from "canvas-confetti";
import helpers from "../../../../lib/crypto";
import { useFunctionContext } from "../../../context/FunctionalContext";

const API_URL = import.meta.env.VITE_API_URL;

const RealtyNewsLetter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [errors, setErrors] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "" });

  const { ORG_ID } = useFunctionContext();

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    let cleanedValue = value.replace(/[^a-zA-Z0-9@._-]/g, "");
    if (name === "email") {
      cleanedValue = cleanedValue.toLowerCase();
    }

    setFormData((prev) => ({
      ...prev,
      [name]: cleanedValue,
    }));
  };

  // Joi validation schema
  const add_leads = (data) => {
    const schema = Joi.object({
      key: Joi.string().required(),
      source: Joi.string().required(),
      email: Joi.string()
        .trim()
        .email({ tlds: { allow: false } })
        .min(6)
        .max(55)
        .required()
        .messages({
          "string.email": "Please enter a valid email address",
        }),
      organisation_id: Joi.string().required(),
      route_action: Joi.number().valid(1).required(),
      status: Joi.string().valid("New").required(),
      admin_type: Joi.string().allow("", null),
      comments: Joi.string().allow("", null),
      next_follow_up: Joi.date().iso().allow("", null),
    });

    return schema.validate(data);
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors("");
    setSuccessMessage("");

    if (!formData.email) {
      setErrors("Please enter your email address.");
      return;
    }

    setLoading(true);

    const payload = {
      key: "scanglobal",
      source: "Realty",
      status: "New",
      organisation_id: ORG_ID,
      route_action: 1,
      admin_type: "1",
      email: formData.email,
      next_follow_up: new Date().toISOString(),
    };

    const { error } = add_leads(payload);
    if (error) {
      setErrors(error.details[0].message);
      setLoading(false);
      return;
    }

    try {
      const encrypted = helpers.encryptobj(payload);

      const response = await axios.post(
        `${API_URL}/admin/add_update_lead`,
        { enc: encrypted },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 200) {
        setSuccessMessage("Submitted successful! 🎉");
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
        setFormData({ email: "" });
      } else {
        setErrors(
          response.data?.message || "Something went wrong on the server."
        );
      }
    } catch (err) {
      console.error("Axios Submit Error:", err);
      setErrors("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Auto-close modal after success
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
        setIsOpen(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  return (
    <>
      <div className="subscribe-wrapper">
        <button className="Btn" onClick={() => setIsOpen(true)}>
          <svg
            width="16px"
            height="16px"
            className="logoIcon "
            viewBox="0 0 16 16"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            // className="bi bi-bell-fill"
          >
            <path d="M8 16a2 2 0 0 0 2-2H6a2 2 0 0 0 2 2zm.995-14.901a1 1 0 1 0-1.99 0A5.002 5.002 0 0 0 3 6c0 1.098-.5 6-2 7h14c-1.5-1-2-5.902-2-7 0-2.42-1.72-4.44-4.005-4.901z" />
          </svg>
          Contact
        </button>
      </div>
      {/* <button
        onClick={() => setIsOpen(true)}
        className={`rounded-lg bg-slate-300 text-slate-900 px-4 py-2 font-semibold hover:text-opacity-80 inline-flex items-center gap-2 transition-colors text-sm md:text-xl ${customClass}`}
      >
        Subscribe Now →
      </button> */}

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="relative bg-slate-900 rounded-2xl shadow-lg p-8 max-w-md w-full text-center"
            >
              <h3 className="text-xl font-semibold mb-4 text-white">
                Join Our Newsletter
              </h3>
              <p className="text-gray-400 mb-6">
                Get exclusive updates on the latest property listings.
              </p>

              <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-transparent border border-gray-700 rounded p-3 text-white focus:outline-none focus:border-gold transition-colors duration-300"
                  required
                  maxLength={55}
                />

                {errors && (
                  <p className="mt-2 text-sm text-red-400 font-medium text-left">
                    {errors}
                  </p>
                )}
                {successMessage && (
                  <p className="mt-2 text-sm text-green-400 font-medium text-left">
                    {successMessage}
                  </p>
                )}

                <button
                  type="submit"
                  className="px-4 py-3 rounded-lg bg-gold hover:bg-gold/85 text-black font-medium transition-all flex items-center justify-center"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    "Submit"
                  )}
                </button>
              </form>

              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-200 transition-colors"
              >
                <X className="size-5" />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default RealtyNewsLetter;
